import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  faEdit,
  faTrash,
  faEye,
  faCheck,
  faTimes,
  faCalendarAlt,
  faCreditCard,
  faMoneyBillWave,
  faSpinner,
  faSortUp,
  faSortDown,
  faSort,
  faCircleHalfStroke,
  faMoneyBill1Wave,
} from '@fortawesome/free-solid-svg-icons';
import {
  getPayments,
  loadMorePayments,
  createPayment,
  updatePayment,
  deletePayment,
} from '@redux/features/paymentsSlice';
import cn from 'classnames';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { formatDateTimeUA } from 'helpers/formatDate';
import { ConfirmDeletionModal } from 'components/ConfirmDeletionModal';
import { TransactionForm } from './components/TransactionForm';
import { MoneyAmount } from 'components/MoneyAmount';
import { PaymentModal } from './PaymentModal';
import { Header } from './components/Header';
import { Button } from 'components/Button';
import styles from './styles.scss';

export const Payments = ({ entity, entityType }) => {
  const dispatch = useDispatch();
  const { payments, isLoading, isLoadingMore, error, meta } = useSelector((state) => state.payments);

  const [createTransactionForPaymentID, setCreateTransactionForPaymentID] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [deletePaymentId, setDeletePaymentId] = useState(null);

  const sentinelRef = useRef(null);

  const [filters, setFilters] = useState({
    search: '',
    paid: '',
    date: '',
    subscriptionID: '',
    entryID: '',
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  });

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const params = {
      page: 1,
      limit: 30,
      [entityType === 'client' ? 'clientID' : 'coachID']: entity.id,
      ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
    };
    dispatch(getPayments(params));
    setCurrentPage(1);
  }, [dispatch, filters, entity.id, entityType]);

  const handleSort = useCallback((field) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'DESC' ? 'ASC' : 'DESC',
    }));
  }, []);

  const handleLoadMore = useCallback(() => {
    if (meta.hasMore && !isLoadingMore) {
      const nextPage = currentPage + 1;
      const params = {
        page: nextPage,
        limit: 30,
        [entityType === 'client' ? 'clientID' : 'coachID']: entity.id,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== '')),
      };
      dispatch(loadMorePayments(params));
      setCurrentPage(nextPage);
    }
  }, [dispatch, meta.hasMore, isLoadingMore, currentPage, filters, entity.id, entityType]);

  const handleCreatePayment = useCallback(() => {
    setSelectedPayment(null);
    setModalMode('create');
    setShowModal(true);
  }, []);

  const handleEditPayment = useCallback((payment) => {
    setSelectedPayment(payment);
    setModalMode('edit');
    setShowModal(true);
  }, []);

  const handleViewPayment = useCallback((payment) => {
    setSelectedPayment(payment);
    setModalMode('view');
    setShowModal(true);
  }, []);

  const handleDeletePayment = useCallback((paymentId) => {
    setDeletePaymentId(paymentId);
    setShowConfirmModal(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (deletePaymentId) {
      dispatch(deletePayment(deletePaymentId));
    }
    setShowConfirmModal(false);
    setDeletePaymentId(null);
  }, [dispatch, deletePaymentId]);

  useInfiniteScroll({
    sentinelRef,
    hasMore: meta.hasMore,
    isLoading: isLoadingMore,
    onLoadMore: handleLoadMore,
    threshold: 0.1,
    rootMargin: '200px',
  });

  const getSortIcon = (field) => {
    if (filters.sortBy !== field) return faSort;
    return filters.sortOrder === 'ASC' ? faSortUp : faSortDown;
  };

  const getPaymentPurpose = (payment) => {
    if (payment.subscriptionID) {
      return {
        type: 'subscription',
        text: 'Оплата пакету',
        icon: faCreditCard,
      };
    }
    if (payment.entryID) {
      return {
        type: 'entry',
        text: 'Оплата заняття',
        icon: faCalendarAlt,
      };
    }
    return {
      type: 'other',
      text: 'Інший платіж',
      icon: faMoneyBillWave,
    };
  };

  const getTotalPaidAmount = (payment) => {
    if (!payment.transactions) return 0;
    return Array.isArray(payment.transactions)
      ? payment.transactions.reduce((sum, t) => sum + (t.amount || 0), 0)
      : payment.transactions.amount || 0;
  };

  const getPaymentProgress = (payment) => {
    if (payment.paid) return 100;
    const totalPaid = getTotalPaidAmount(payment);
    const progress = (totalPaid / payment.amount) * 100;
    return Math.min(100, Math.max(0, progress));
  };

  return (
    <div className={styles.paymentsPage}>
      <Header handleCreatePayment={handleCreatePayment} filters={filters} setFilters={setFilters} />
      {error && (
        <div className={styles.errorMessage}>
          <FontAwesomeIcon icon={faTimes} />
          {error}
        </div>
      )}

      <div className={styles.tableContainer}>
        {isLoading && payments.length === 0 ? (
          <div className={styles.loadingContainer}>
            <FontAwesomeIcon icon={faSpinner} spin className={styles.loadingIcon} />
            <p>Завантаження рахунків...</p>
          </div>
        ) : (
          <>
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th>Призначення</th>
                  <th onClick={() => handleSort('amount')} className={styles.sortable}>
                    <div>
                      Сума
                      <FontAwesomeIcon icon={getSortIcon('amount')} className={styles.sortIcon} />
                    </div>
                  </th>
                  <th>Прогрес оплати</th>
                  <th>Транзакції</th>
                  <th onClick={() => handleSort('paid')} className={styles.sortable}>
                    <div>
                      Статус
                      <FontAwesomeIcon icon={getSortIcon('paid')} className={styles.sortIcon} />
                    </div>
                  </th>
                  <th onClick={() => handleSort('dueTime')} className={styles.sortable}>
                    <div>
                      Термін оплати
                      <FontAwesomeIcon icon={getSortIcon('dueTime')} className={styles.sortIcon} />
                    </div>
                  </th>
                  <th>Дії</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => {
                  const purpose = getPaymentPurpose(payment);
                  const progress = getPaymentProgress(payment);
                  const totalPaid = getTotalPaidAmount(payment);

                  return (
                    <tr key={payment.id} className={styles.tableRow}>
                      <td>
                        <div className={styles.purposeInfo}>
                          <FontAwesomeIcon icon={purpose.icon} className={styles.purposeIcon} />
                          <span>{payment?.description || purpose.text}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.totalAmount}>
                          <MoneyAmount value={payment.amount} />
                        </div>
                        {totalPaid > 0 && totalPaid < payment.amount && (
                          <div className={styles.paidAmount}>
                            Оплачено: <MoneyAmount value={totalPaid} />
                          </div>
                        )}
                      </td>
                      <td>
                        <div className={styles.progressContainer}>
                          <div className={styles.progressBar}>
                            <div
                              className={styles.progressFill}
                              style={{
                                width: `${progress}%`,
                                backgroundColor: progress === 100 ? '#059669' : progress > 0 ? '#f59e0b' : '#e5e7eb',
                              }}
                            />
                          </div>
                          <span className={styles.progressText}>{Math.round(progress)}%</span>
                        </div>
                      </td>
                      <td className={styles.transactionsCell}>
                        {payment.transactions && payment.transactions.length > 0 ? (
                          <div className={styles.transactionsList}>
                            {payment.transactions.slice(0, 2).map((transaction, index) => (
                              <div key={transaction.id || index} className={styles.transactionItem}>
                                <span className={styles.transactionAmount}>
                                  <MoneyAmount value={transaction.amount} />
                                </span>
                                <span className={styles.transactionMethod}>{transaction.method}</span>
                              </div>
                            ))}
                            {payment.transactions.length > 2 && (
                              <div className={styles.moreTransactions}>+{payment.transactions.length - 2} ще</div>
                            )}
                          </div>
                        ) : (
                          <span className={styles.noTransactions}>Немає транзакцій</span>
                        )}
                      </td>
                      <td>
                        <span
                          className={cn(styles.statusBadge, {
                            [styles.partial]: !payment.paid && progress > 0,
                            [styles.unpaid]: !payment.paid,
                            [styles.paid]: payment.paid,
                          })}
                        >
                          <FontAwesomeIcon
                            icon={payment.paid ? faCheck : progress > 0 ? faCircleHalfStroke : faTimes}
                          />
                          {payment.paid ? 'Оплачено' : progress > 0 ? 'Частково оплачено' : 'Не оплачено'}
                        </span>
                      </td>
                      <td className={styles.dateCell}>{formatDateTimeUA(payment.dueTime, true)}</td>
                      <td className={styles.actionsCell}>
                        <div className={styles.actions}>
                          {!payment.paid && (
                            <>
                              <button
                                className={styles.actionButton}
                                onClick={() => setCreateTransactionForPaymentID(payment.id)}
                                title='Внести оплату'
                              >
                                <FontAwesomeIcon icon={faMoneyBill1Wave} />
                              </button>
                              {createTransactionForPaymentID === payment.id && (
                                <TransactionForm
                                  payment={payment}
                                  entityID={entity.id}
                                  entityType={entityType}
                                  entityBalance={entity?.balance}
                                  onClose={() => setCreateTransactionForPaymentID(null)}
                                />
                              )}
                            </>
                          )}
                          <button
                            className={styles.actionButton}
                            onClick={() => handleViewPayment(payment)}
                            title='Переглянути'
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            className={styles.actionButton}
                            onClick={() => handleEditPayment(payment)}
                            title='Редагувати'
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            className={cn(styles.actionButton, styles.danger)}
                            onClick={() => handleDeletePayment(payment.id)}
                            title='Видалити'
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {payments.length === 0 && !isLoading && (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faCreditCard} className={styles.emptyIcon} />
                <h3>Рахунків не знайдено</h3>
                <p>Створіть перший рахунок або змініть критерії пошуку</p>
                <Button onClick={handleCreatePayment} smallStyle>
                  Створити рахунок
                </Button>
              </div>
            )}

            {meta.hasMore && (
              <div className={styles.loadMoreContainer} ref={sentinelRef}>
                <Button onClick={handleLoadMore} disabled={isLoadingMore} ghostStyle smallStyle>
                  {isLoadingMore ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Завантаження...
                    </>
                  ) : (
                    'Завантажити ще'
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <PaymentModal
        isShowModal={showModal}
        mode={modalMode}
        payment={selectedPayment}
        entity={entity}
        entityType={entityType}
        onClose={() => setShowModal(false)}
        onSave={(paymentData) => {
          if (modalMode === 'create') {
            dispatch(createPayment(paymentData));
          } else if (modalMode === 'edit') {
            dispatch(updatePayment({ id: selectedPayment.id, ...paymentData }));
          }
          setShowModal(false);
        }}
      />

      <ConfirmDeletionModal
        isOpen={showConfirmModal}
        setIsOpen={setShowConfirmModal}
        elementName='рахунок'
        onConfirm={confirmDelete}
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  );
};
